import { useState, useRef, useEffect } from "react";
import { TiLocationArrow } from "react-icons/ti";
import { MessageCircle } from "lucide-react";
import AnimatedContent from "./AnimatedContent";
import Squares from "./Squares";
import AIChatbot from "./AIChatbot";
import FullscreenButton from "./FullscreenButton";

// Scroll-triggered animated title component
const AnimatedTitle = ({ children, className = "", onAnimationComplete, as = "h1" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const titleRef = useRef(null);
  const Component = as;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
          // Trigger typewriter after title animation completes (1s duration)
          setTimeout(() => {
            if (onAnimationComplete) {
              onAnimationComplete();
            }
          }, 1000);
        }
      },
      { threshold: 0.3 }
    );

    if (titleRef.current) {
      observer.observe(titleRef.current);
    }

    return () => {
      if (titleRef.current) {
        observer.unobserve(titleRef.current);
      }
    };
  }, [isVisible, onAnimationComplete]);

  return (
    <Component 
      ref={titleRef}
      className={`${className} ${isVisible ? 'feature-title-animate' : 'opacity-0'}`}
    >
      {children}
    </Component>
  );
};

// Typewriter effect component triggered externally
const TypewriterText = ({ text, className = "", shouldStart = false }) => {
  const [visibleWords, setVisibleWords] = useState(0);
  const words = text.split(' ');

  useEffect(() => {
    if (shouldStart) {
      // Reset visible words first
      setVisibleWords(0);
      
      words.forEach((_, index) => {
        setTimeout(() => {
          setVisibleWords(index + 1);
        }, index * 100); // 100ms delay between words
      });
    }
  }, [shouldStart, words.length]);

  return (
    <span className={className}>
      {words.map((word, index) => (
        <span
          key={index}
          className={`word ${index < visibleWords ? 'opacity-100' : 'opacity-0'}`}
          style={{ 
            marginRight: index < words.length - 1 ? '0.25rem' : '0',
            transition: 'opacity 0.3s ease-out, transform 0.3s ease-out',
            transform: index < visibleWords ? 'translateX(0)' : 'translateX(-10px)',
            display: 'inline-block'
          }}
        >
          {word}
        </span>
      ))}
    </span>
  );
};

export const BentoTilt = ({ children, className = "" }) => {
  const [transformStyle, setTransformStyle] = useState("");
  const itemRef = useRef(null);

  const handleMouseMove = (event) => {
    if (!itemRef.current) return;

    const { left, top, width, height } =
      itemRef.current.getBoundingClientRect();

    const relativeX = (event.clientX - left) / width;
    const relativeY = (event.clientY - top) / height;

    const tiltX = (relativeY - 0.5) * 5;
    const tiltY = (relativeX - 0.5) * -5;

    const newTransform = `perspective(700px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) scale3d(.95, .95, .95)`;
    setTransformStyle(newTransform);
  };

  const handleMouseLeave = () => {
    setTransformStyle("");
  };

  return (
    <div
      ref={itemRef}
      className={className}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ transform: transformStyle }}
    >
      {children}
    </div>
  );
};

export const BentoCard = ({ src, title, description, isComingSoon, rightAlign, videoLoaded, videoId }) => {
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [hoverOpacity, setHoverOpacity] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const hoverButtonRef = useRef(null);
  const videoRef = useRef(null);

  const handleMouseMove = (event) => {
    if (!hoverButtonRef.current) return;
    const rect = hoverButtonRef.current.getBoundingClientRect();

    setCursorPosition({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    });
  };

  const handleMouseEnter = () => {
    setHoverOpacity(1);
    setIsHovered(true);
  };
  
  const handleMouseLeave = () => {
    setHoverOpacity(0);
    setIsHovered(false);
  };

  const handleVideoLoad = () => {
    console.log(`[Features] Video loaded: ${videoId}`);
  };

  const handleVideoError = (e) => {
    console.error(`[Features] Video error: ${videoId}`, e);
  };


  return (
    <div 
      className="relative size-full"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {videoLoaded ? (
        <video
          ref={videoRef}
          src={src}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          className="absolute left-0 top-0 size-full object-cover object-center"
          onLoadedData={handleVideoLoad}
          onError={handleVideoError}
        >
          <track
            kind="captions"
            src="/captions/workflow-captions.vtt"
            srcLang="en"
            label="English captions"
            default
          />
        </video>
      ) : (
        <div className="absolute left-0 top-0 size-full bg-gray-900" />
      )}
      
      <div className="relative z-10 flex size-full flex-col justify-between p-5 text-blue-50">
        <div>
          {title && (
            <h1 className={rightAlign ? "bento-title-right special-font" : "bento-title special-font"}>
              {title}
            </h1>
          )}
          {description && (
            <p className={`mt-3 max-w-64 text-xs md:text-sm ${rightAlign ? "bento-description-right ml-auto" : ""}`}>
              {description}
            </p>
          )}
        </div>

        {/* Fullscreen button */}
        <FullscreenButton 
          videoRef={videoRef}
          isVisible={isHovered && videoLoaded}
          className=""
        />

        {isComingSoon && (
          <div
            ref={hoverButtonRef}
            onMouseMove={handleMouseMove}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className={`border-hsla relative flex w-fit cursor-pointer items-center gap-1 overflow-hidden rounded-full bg-black px-5 py-2 text-xs uppercase text-white/20 ${rightAlign ? "ml-auto" : ""}`}
          >
            {/* Radial gradient hover effect */}
            <div
              className="pointer-events-none absolute -inset-px opacity-0 transition duration-300"
              style={{
                opacity: hoverOpacity,
                background: `radial-gradient(100px circle at ${cursorPosition.x}px ${cursorPosition.y}px, #656fe288, #00000026)`,
              }}
            />
            <TiLocationArrow className="relative z-20" />
            <p className="relative z-20">coming soon</p>
          </div>
        )}
      </div>
    </div>
  );
};

const Features = () => {
  const [videosLoaded, setVideosLoaded] = useState({
    'feature-1': false,
    'feature-2': false,
    'feature-5': false  // Added for Frame Processor
  });
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  
  // Typewriter animation triggers
  const [workflowsTypewriterStart, setWorkflowsTypewriterStart] = useState(false);
  const [knowledgeBasesTypewriterStart, setKnowledgeBasesTypewriterStart] = useState(false);
  const [automateWorkflowsTypewriterStart, setAutomateWorkflowsTypewriterStart] = useState(false);
  
  const sectionRef = useRef(null);
  const hasPreloadedFeature1 = useRef(false);

  // Listen for About section animation completion
  useEffect(() => {
    const handlePreload = () => {
      if (!hasPreloadedFeature1.current) {
        console.log('[Features] About animation complete, preloading feature-1');
        hasPreloadedFeature1.current = true;
        setVideosLoaded(prev => ({ ...prev, 'feature-1': true }));
      }
    };
    
    window.addEventListener('preloadFeatures', handlePreload);
    return () => window.removeEventListener('preloadFeatures', handlePreload);
  }, []);

  // Intersection Observer for section visibility
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const isInView = entry.isIntersecting;
        
        if (isInView && entry.intersectionRatio > 0.1) {
          console.log('[Features] Section in view, loading all videos');
          // Load all videos when section is visible
          setVideosLoaded({
            'feature-1': true,
            'feature-2': true,
            'feature-5': true  // Added for Frame Processor
          });
        } else if (!isInView && entry.intersectionRatio === 0) {
          console.log('[Features] Section out of view, unloading videos');
          // Unload all videos when section is completely out of view
          // Reset the preload flag so feature-1 can be preloaded again
          hasPreloadedFeature1.current = false;
          setVideosLoaded({
            'feature-1': false,
            'feature-2': false,
            'feature-5': false  // Added for Frame Processor
          });
        }
      },
      { 
        threshold: [0, 0.1, 0.5],
        rootMargin: '-10% 0px' // Slightly tighter margins for unloading
      }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  // Log current state for debugging
  useEffect(() => {
    console.log('[Features] Videos loaded state:', videosLoaded);
  }, [videosLoaded]);

  return (
    <section ref={sectionRef} className="relative bg-black pb-20 md:pb-44">
      {/* Animated Grid Background */}
      <div className="absolute inset-0 z-0">
        <Squares 
          speed={0.5} 
          squareSize={40}
          direction='diagonal'
          borderColor='rgba(255, 255, 255, 0.1)'
          hoverFillColor='rgba(34, 34, 34, 0.5)'
        />
      </div>
      
      <div className="container relative z-10 mx-auto px-3 md:px-10">
        {/* AI Chatbot Section */}
        <AnimatedContent
          distance={120}
          direction="vertical"
          reverse={false}
          duration={1.0}
          ease="power2.out"
          initialOpacity={0.1}
          animateOpacity
          scale={0.95}
          threshold={0.2}
          delay={0.1}
        >
          <div className="px-5 py-16">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="mb-4 font-circular-web text-2xl text-blue-50 md:text-3xl">
                <span className="animate-gradient bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                  Ask the Knowledge Base
                </span>
              </h2>
              <p className="mb-8 font-circular-web text-lg text-blue-50 opacity-70">
                Powered by LightRAG, explore our workflows and capabilities through intelligent conversation.
              </p>
              
              {/* Chat Trigger Button */}
              <button
                onClick={() => setIsChatbotOpen(true)}
                className="group relative inline-flex items-center gap-3 overflow-hidden rounded-xl px-8 py-4 transition-all duration-300 hover:scale-105"
              >
                {/* Animated Border Container */}
                <div className="absolute -inset-[2px] overflow-hidden rounded-xl">
                  {/* Base gradient background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20" />
                  
                  {/* Saber Energy Border */}
                  <svg className="absolute inset-0 size-full">
                    <defs>
                      {/* Energy gradient that shifts colors */}
                      <linearGradient id="buttonEnergyBeam" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#3B82F6" stopOpacity="0" />
                        <stop offset="10%" stopColor="#3B82F6" stopOpacity="0.3" />
                        <stop offset="30%" stopColor="#8B5CF6" stopOpacity="0.6" />
                        <stop offset="50%" stopColor="#EC4899" stopOpacity="1">
                          <animate attributeName="stop-color" 
                            values="#EC4899;#F59E0B;#EC4899" 
                            dur="2s" 
                            repeatCount="indefinite" />
                        </stop>
                        <stop offset="70%" stopColor="#8B5CF6" stopOpacity="0.6" />
                        <stop offset="90%" stopColor="#3B82F6" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
                      </linearGradient>
                      
                      {/* Intense glow filter */}
                      <filter id="buttonSaberGlow">
                        <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                        <feGaussianBlur stdDeviation="4" result="coloredBlur2"/>
                        <feMerge>
                          <feMergeNode in="coloredBlur2"/>
                          <feMergeNode in="coloredBlur"/>
                          <feMergeNode in="SourceGraphic"/>
                        </feMerge>
                      </filter>
                    </defs>
                    
                    {/* Main energy beam */}
                    <rect
                      x="1"
                      y="1"
                      width="98%"
                      height="98%"
                      rx="12"
                      fill="none"
                      stroke="url(#buttonEnergyBeam)"
                      strokeWidth="2"
                      strokeDasharray="60 160"
                      filter="url(#buttonSaberGlow)"
                      className="animate-saber-beam"
                    />
                    
                    {/* Inner bright core */}
                    <rect
                      x="2"
                      y="2"
                      width="96%"
                      height="96%"
                      rx="11"
                      fill="none"
                      stroke="white"
                      strokeWidth="1"
                      strokeDasharray="40 180"
                      className="animate-saber-beam"
                      opacity="0.7"
                    />
                  </svg>
                </div>
                
                {/* Button Content */}
                <div className="relative flex items-center gap-3 rounded-xl bg-gray-900/90 px-6 py-3 backdrop-blur-xl">
                  <MessageCircle className="size-6 text-white" />
                  <span className="text-lg font-semibold text-white">Start Conversation</span>
                </div>
              </button>
            </div>
          </div>
        </AnimatedContent>

        <AnimatedContent
          distance={150}
          direction="vertical"
          reverse={false}
          duration={1.2}
          ease="bounce.out"
          initialOpacity={0.2}
          animateOpacity
          scale={1.1}
          threshold={0.2}
          delay={0.3}
        >
          <div className="w-full px-5 py-32 text-center">
            <AnimatedTitle 
              className="bento-title special-font mb-4 text-center text-white"
              onAnimationComplete={() => setWorkflowsTypewriterStart(true)}
              as="h2"
            >
              Behind the W<b>o</b>rkfl<b>o</b>ws
            </AnimatedTitle>
            <p className="feature-description-animate w-full max-w-none font-circular-web text-lg text-white/50">
              <TypewriterText 
                text="Real automation workflows in various stages—debugging errors, building integrations, collaborating with AI assistants, and creating supporting web applications. An authentic look at how creative thinking systematically transforms into working solutions."
                shouldStart={workflowsTypewriterStart}
              />
            </p>
          </div>
        </AnimatedContent>

        <AnimatedContent
          distance={100}
          direction="horizontal"
          reverse={false}
          duration={1.0}
          ease="power3.out"
          initialOpacity={0}
          animateOpacity
          scale={0.95}
          threshold={0.3}
          delay={0.6}
        >
          <div>
            <BentoTilt className="bento-16x9 mb-7 md:mb-16">
              <BentoCard
                src="https://storage.googleapis.com/tll-videos-for-web/LightragBuild_WTextOverlays.mp4"
                videoId="feature-1"
                videoLoaded={videosLoaded['feature-1']}
                isComingSoon={false}
                rightAlign
              />
            </BentoTilt>
            <AnimatedTitle 
              className="bento-title special-font mb-4 text-right text-white"
              onAnimationComplete={() => setKnowledgeBasesTypewriterStart(true)}
              as="h3"
            >
              Kn<b>o</b>wledge B<b>a</b>ses
            </AnimatedTitle>
            <p className="feature-description-animate mb-6 ml-auto max-w-md text-right font-circular-web text-lg text-white/50">
              <TypewriterText 
                text="Document ingestion in a wide variety of formats to build highly accurate, queryable retrieval systems that provide comprehensive information on everything in the database."
                shouldStart={knowledgeBasesTypewriterStart}
              />
            </p>
          </div>
        </AnimatedContent>

        {/* Frame Processor Workflow Section */}
        {/* 
        <BentoTilt className="border-hsla relative mb-7 md:mb-16 h-96 w-full overflow-hidden rounded-md md:h-[65vh]">
          <BentoCard
            src="YOUR_FRAME_PROCESSOR_VIDEO_URL_HERE"  // Replace with your video URL
            videoId="feature-5"
            videoLoaded={videosLoaded['feature-5']}
            title={
              <>
                Fr<b>a</b>me Pr<b>o</b>cessor W<b>o</b>rkflow
              </>
            }
            description="Processing and analyzing video frames with intelligent automation workflows."
            isComingSoon={false}
            rightAlign={false}  // You can change this to true if you want right alignment
          />
        </BentoTilt>
        */}

        <AnimatedContent
          distance={120}
          direction="vertical"
          reverse={false}
          duration={1.3}
          ease="power2.out"
          initialOpacity={0.1}
          animateOpacity
          scale={0.9}
          threshold={0.25}
          delay={0.9}
        >
          <div>
            <AnimatedTitle 
              className="bento-title special-font mb-4 text-white"
              onAnimationComplete={() => setAutomateWorkflowsTypewriterStart(true)}
              as="h3"
            >
              <b>A</b>utomate W<b>o</b>rkflows
            </AnimatedTitle>
            <p className="feature-description-animate mb-6 max-w-md font-circular-web text-lg text-white/50">
              <TypewriterText 
                text="Building and debugging multi-tool automation systems with AI-assisted development. Over 100 workflows created to handle diverse tasks across industries, from data processing to content generation."
                shouldStart={automateWorkflowsTypewriterStart}
              />
            </p>
            <div className="grid-rows-auto grid h-auto w-full grid-cols-2 gap-7 md:grid-rows-2">
              <BentoTilt className="bento-tilt_1 h-[66vw] md:row-span-2 md:h-[700px]">
                <BentoCard
                  src="https://storage.googleapis.com/tll-videos-for-web/n8n_WorkingVideo_V2.mp4"
                  videoId="feature-2"
                  videoLoaded={videosLoaded['feature-2']}
                  isComingSoon={false}
                />
              </BentoTilt>

            </div>
          </div>
        </AnimatedContent>
      </div>
      
      {/* AI Chatbot Component */}
      <AIChatbot 
        isOpen={isChatbotOpen} 
        onClose={() => setIsChatbotOpen(false)} 
      />
    </section>
  );
};

export default Features;